import { ClientNotesPanel } from '@app/features/notes';
import { APP_PREFIX, CLIENTS_PREFIX, EDIT_CLIENT } from '@app/routes/urls';
import { useAuthContext } from '@context/auth/useAuthContext';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { newArrangementPath } from '@feat-service-requests/serviceRequestLinks';
import { Alert, Skeleton } from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CareTeamPanel from './CareTeamPanel';
import CareTimelinePanel from './CareTimelinePanel';
import { CardVisit, FolderFile, nextVisit } from './clientCard.model';
import ClientCardHeader from './ClientCardHeader';
import ClientFolderPanel from './ClientFolderPanel';
import { isFolderPdf, openFolderPdf } from './folderFile';
import AttachmentDrawer from './drawers/AttachmentDrawer';
import BillingDrawer from './drawers/BillingDrawer';
import VisitDrawer from './drawers/VisitDrawer';
import useClientCard from './useClientCard';
import useFolderDownload from './useFolderDownload';
import VitalsPanel from './VitalsPanel';

/**
 * The client card: who the client is, who looks after them, what has been measured,
 * what has happened, what caregivers wrote, and every file on record.
 *
 * One fetch of the client's visits feeds five of the six blocks, so nothing on the
 * page can disagree with anything else about what happened. The drawers open over
 * data already in hand — only the billing ledger fetches, and only when opened.
 */
/**
 * Who is reading the card.
 *
 * `doctor` is not "staff with fewer permissions": it is a clinical reader of one
 * patient's record. It drops the notes panel and the edit action, and it never
 * asks for billing. Passed in rather than derived from the user's roles here,
 * because the same account can hold both personas and only the ROUTE knows which
 * one is being used — the same rule the backend applies to its `/doctor` paths.
 */
export type ClientCardAudience = 'staff' | 'doctor';

// Both panels in the timeline row pin their body to this, so the two cards are always
// the same height however many rows each happens to have.
const CARD_ROW_BODY_HEIGHT = 650;

const ClientCard: React.FC<{ clientId: number; audience: ClientCardAudience }> = ({ clientId, audience }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const isDoctor = audience === 'doctor';

  // The same predicate RoutingProvider uses to decide whether the billing module
  // exists for this user at all. Office administrators inherit admin access but not
  // billing, and its endpoints refuse them. `isDoctor` is an independent AND, not a
  // shortcut: an account that is both a doctor and an administrator still gets no
  // money on the doctor's route, which is the whole point of asking the route.
  const canViewBilling = !isDoctor && !!user?.isAdmin;

  const {
    client,
    isClientLoading,
    clientError,
    visits,
    visitsTotal,
    areVisitsLoading,
    visitsError,
    clientAttachments,
    areAttachmentsLoading,
    attachmentsError,
    balance,
    lastMovement,
    hasNoBillingAccount,
    isBalanceLoading,
    // Named apart from the global `window`, and handed to the panels that state a
    // count so the count can say what it counted over.
    window: cardWindow,
  } = useClientCard(clientId, { includeBilling: canViewBilling });

  const { download, isDownloading } = useFolderDownload();
  const { handleError } = useSmartErrorHandling();

  const [openVisit, setOpenVisit] = useState<CardVisit | null>(null);
  const [openFile, setOpenFile] = useState<FolderFile | null>(null);
  const [isBillingOpen, setIsBillingOpen] = useState(false);

  const next = useMemo(() => nextVisit(visits), [visits]);

  if (isClientLoading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  if (clientError || !client) {
    return <Alert type="error" showIcon message={t('client-card-load-error')} />;
  }

  const clientName = `${client.firstName} ${client.lastName}`.trim();

  // The shell's <main> is `overflow-hidden`, so this page brings its own scroller. The negative
  // margins pull it over <main>'s padding so the wheel also scrolls from the gutters beside the cards.
  return (
    <div className="-mx-4 -my-2 min-h-0 flex-1 overflow-y-auto px-4 py-2">
      <div className="flex flex-col gap-3 sm:gap-4">
        <ClientCardHeader
          client={client}
          visits={visits}
          next={next}
          balance={balance}
          hasNoBillingAccount={hasNoBillingAccount}
          isBalanceLoading={isBalanceLoading}
          canViewBilling={canViewBilling}
          onOpenNext={() => next && setOpenVisit(next)}
          onOpenBilling={() => setIsBillingOpen(true)}
          // The staff client form is not in the doctor's route tree, so offering
          // it would send them to a 404. A doctor reads this record.
          onEdit={
            isDoctor
              ? undefined
              : () => navigate(`/${APP_PREFIX}/${CLIENTS_PREFIX}/${EDIT_CLIENT.replace(':id', String(clientId))}`)
          }
          // Same reasoning as `onEdit`: the authoring screen is staff-only and is not in
          // the doctor's route tree.
          onNewRequest={isDoctor ? undefined : () => navigate(newArrangementPath(clientId))}
        />

        {/* One error for the whole visits fetch, and the blocks it feeds are withheld
            rather than drawn empty: an empty timeline beside an empty notes panel is
            indistinguishable from a client who has never been visited. */}
        {visitsError && <Alert type="error" showIcon message={t('client-card-visits-error')} />}

        {areVisitsLoading ? (
          <Skeleton active paragraph={{ rows: 10 }} />
        ) : visitsError ? null : (
          <>
            <div className="grid grid-cols-1 items-stretch gap-3 sm:gap-4 xl:grid-cols-3">
              {/* The care team is the office's roster - who is going, and when. A doctor
                  reads this card for the patient, not for the staffing, so it is theirs
                  to skip and the vitals take the full row. */}
              {!isDoctor && <CareTeamPanel client={client} visits={visits} />}
              <VitalsPanel
                visits={visits}
                window={cardWindow}
                className={isDoctor ? 'xl:col-span-3' : 'xl:col-span-2'}
              />
            </div>

            <div className="grid grid-cols-1 items-stretch gap-3 sm:gap-4 xl:grid-cols-3">
              <CareTimelinePanel
                visits={visits}
                total={visitsTotal}
                window={cardWindow}
                onOpenVisit={setOpenVisit}
                className="xl:col-span-2"
                bodyMaxHeight={CARD_ROW_BODY_HEIGHT}
              />
              {/* The doctor gets this too. A note addressed to them is the office
                  talking to them about this patient, and before this they had nowhere
                  to read it - row-level security allowed it and no screen showed it.
                  What they SEE is still the policy's decision: a caregiver's note is
                  addressed to {adm,cgv} and never appears here. */}
              <ClientNotesPanel clientId={clientId} bodyMaxHeight={CARD_ROW_BODY_HEIGHT} />
            </div>

            <ClientFolderPanel
              clientId={clientId}
              clientAttachments={clientAttachments}
              visits={visits}
              isLoading={areAttachmentsLoading}
              hasError={!!attachmentsError}
              onOpenFile={setOpenFile}
            />
          </>
        )}
      </div>

      <VisitDrawer
        visit={openVisit}
        clientId={clientId}
        onClose={() => setOpenVisit(null)}
        onOpenFile={(file) => {
          if (isFolderPdf(file)) {
            openFolderPdf(file).catch((error) => handleError(error as Error));
            return;
          }
          setOpenVisit(null);
          setOpenFile(file);
        }}
      />

      <AttachmentDrawer
        file={openFile}
        visits={visits}
        onClose={() => setOpenFile(null)}
        onOpenVisit={(visit) => {
          setOpenFile(null);
          setOpenVisit(visit);
        }}
        onDownload={download}
        isDownloading={!!openFile && isDownloading(openFile)}
      />

      <BillingDrawer
        open={isBillingOpen && canViewBilling}
        clientId={clientId}
        clientName={clientName}
        balance={balance}
        lastMovement={lastMovement}
        onClose={() => setIsBillingOpen(false)}
      />
    </div>
  );
};

export default ClientCard;
